import React, { useMemo } from 'react';
import AvatarComposite from '../../Avatar/AvatarComposite';
import { AVATAR_PROFILES } from '../../Avatar/avatarModel';
import { LiveCountdownBar } from './CountdownBar';
import { ImageLoader } from '../ImageLoader';
import { MEDIA_VARIANTS } from '../../../media';
import { getCombatVisualTiming } from './combatEvents';

export const DEFAULT_ENEMY_TYPE_COLOR = '#9ca3af';

const CORE_STAT_ALIASES = Object.freeze({
  damage: ['damage', 'dmg'],
  armor: ['armor', 'armour', 'shield'],
  health: ['health', 'hp'],
});

export function normalizeEnemyTypeColor(color) {
  return /^#[0-9a-f]{6}$/i.test(color || '') ? color : DEFAULT_ENEMY_TYPE_COLOR;
}

function normalizeStatName(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function findCoreStat(stats, coreKey) {
  const aliases = CORE_STAT_ALIASES[coreKey] || [];
  return stats.find(stat => {
    const key = normalizeStatName(stat.key);
    const label = normalizeStatName(stat.label);
    return aliases.some(alias => key === alias || label === alias);
  });
}

function formatHealth(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0';
  return Number(numeric.toFixed(2)).toString();
}

function formatEffectAmount(effect) {
  const amount = Number(effect.amount);
  const formatted = Number.isFinite(amount) ? Number(amount.toFixed(2)).toString() : '0';
  const count = Number(effect.count) > 1 ? ` ×${effect.count}` : '';
  if (effect.kind === 'dodge') return `DODGE${count}`;
  if (effect.kind === 'heal') return `+${formatted}${count}`;
  if (effect.kind === 'crit') return `CRIT −${formatted}${count}`;
  return `−${formatted}${count}`;
}

function latestEffect(effects, predicate) {
  return effects.reduce((latest, effect) => (
    predicate(effect) && (!latest || effect.sequence > latest.sequence) ? effect : latest
  ), null);
}

function CombatStats({ stats, side }) {
  const damage = findCoreStat(stats, 'damage');
  const armor = findCoreStat(stats, 'armor');
  const hiddenCoreKeys = new Set([
    damage?.key,
    armor?.key,
    findCoreStat(stats, 'health')?.key,
  ].filter(Boolean));
  const details = stats.filter(stat => !hiddenCoreKeys.has(stat.key));

  return (
    <div className="combat-fighter__stats">
      <div className="combat-fighter__stat-chips" aria-label={`${side} core combat stats`}>
        <span className="combat-fighter__stat-chip"><b>DMG</b> {damage?.value ?? '0'}</span>
        <span className="combat-fighter__stat-chip"><b>ARM</b> {armor?.value ?? '0'}</span>
      </div>
      <details className="combat-fighter__details">
        <summary>Details</summary>
        <div className="combat-fighter__details-panel">
          {details.length > 0 ? (
            <dl>
              {details.map(stat => (
                <div key={`${side}-detail-${stat.key}`}>
                  <dt>{stat.label}</dt>
                  <dd>{stat.value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p>No additional combat stats.</p>
          )}
        </div>
      </details>
    </div>
  );
}

function CombatFighter({
  side,
  name,
  currentHp,
  maxHp,
  hpPercent,
  stats,
  timer,
  effects,
  avatarConfig,
  avatarSelection,
  enemy,
  level,
}) {
  const numericLevel = Number(level);
  const displayName = Number.isInteger(numericLevel) && numericLevel > 0
    ? `${name} · Lv. ${numericLevel}`
    : name;
  const motionEffect = latestEffect(
    effects,
    effect => (
      effect.target === side
      || (effect.attacker === side && ['hit', 'crit', 'dodge'].includes(effect.kind))
    ),
  );
  const isTargetEffect = motionEffect?.target === side;
  const motionRole = isTargetEffect ? motionEffect?.kind : (motionEffect ? 'attack' : null);
  const motionSlot = motionEffect && motionEffect.sequence % 2 === 0 ? 'b' : 'a';
  const motionClasses = motionRole
    ? ` combat-fighter__art-frame--${motionRole} combat-fighter__art-frame--motion-${motionSlot}`
    : '';
  const motionTiming = motionEffect ? getCombatVisualTiming(motionEffect.kind) : null;
  const motionStyle = motionTiming ? {
    '--combat-motion-delay': `${isTargetEffect ? motionTiming.reactionDelayMs : 0}ms`,
    '--combat-motion-duration': `${isTargetEffect
      ? motionTiming.reactionDurationMs
      : motionTiming.attackDurationMs}ms`,
  } : undefined;

  return (
    <article className={`combat-fighter combat-fighter--${side}`}>
      <h3 className="combat-fighter__name">{displayName}</h3>
      <div className={`combat-fighter__art-frame${motionClasses}`} style={motionStyle}>
        {side === 'player' ? (
          <AvatarComposite
            className="combat-fighter__avatar"
            config={avatarConfig}
            decorative
            renderProfile={AVATAR_PROFILES.PORTRAIT}
            selection={avatarSelection}
          />
        ) : (
          <ImageLoader
            className="combat-fighter__enemy-art"
            imageId={enemy?.imageId}
            animatedMediaId={enemy?.animatedMediaId || enemy?.animatedImageId}
            variant={MEDIA_VARIANTS.PICTURE}
            alt=""
          />
        )}
        <div className="combat-fighter__feedback" aria-hidden="true">
          {effects.filter(effect => effect.target === side).map((effect) => {
            const timing = getCombatVisualTiming(effect.kind);
            return (
              <span
                className={`combat-fighter__callout combat-fighter__callout--${effect.kind}`}
                key={effect.id}
                style={{
                  '--combat-callout-delay': `${timing.feedbackDelayMs}ms`,
                  '--combat-callout-duration': `${timing.feedbackDurationMs}ms`,
                }}
              >
                {formatEffectAmount(effect)}
              </span>
            );
          })}
        </div>
      </div>

      <div className="combat-fighter__vitals">
        <div className="combat-fighter__hp-line">
          <span>HP</span>
          <span>{formatHealth(currentHp)} / {formatHealth(maxHp)}</span>
        </div>
        <div className="combat-overlay__bar" data-type="hp">
          <div style={{ width: `${Math.min(Math.max(hpPercent, 0), 100)}%` }} />
        </div>
      </div>

      <CombatStats side={side} stats={stats} />

      {timer && (
        <LiveCountdownBar
          label="Next attack"
          timer={timer}
          variant={side}
        />
      )}
    </article>
  );
}

function CombatOverlayComponent({
  player,
  enemy,
  enemyLevel,
  enemyTypeColor,
  currentHpPercentage,
  userMaxHp,
  enemyHpPercent,
  enemyHealthMax,
  playerCombatStats,
  enemyCombatStats,
  recentLog,
  combatEffects,
  avatarConfig,
  avatarSelection,
  playerTimer,
  enemyTimer,
  fleeProgress,
  fleeAction,
  fleeLabel,
  onSetDesiredAction,
}) {
  const safeRecentLog = useMemo(() => recentLog ?? [], [recentLog]);
  const safeEffects = useMemo(() => combatEffects ?? [], [combatEffects]);
  const latestLog = safeRecentLog.slice(-3);
  const earlierLog = safeRecentLog.slice(0, -3);
  const enemyOutlineColor = normalizeEnemyTypeColor(enemyTypeColor);

  return (
    <div className="combat-overlay" role="region" aria-labelledby="combat-overlay-title">
      <section
        className="combat-overlay__inner"
        style={{ '--enemy-type-color': enemyOutlineColor }}
      >
        <header className="combat-overlay__header">
          <div>
            <div className="combat-overlay__eyebrow">Live encounter</div>
            <h2 id="combat-overlay-title">Engagement</h2>
          </div>
          <div className="combat-overlay__status">Server synchronized</div>
        </header>

        <div className="combat-overlay__duo">
          <CombatFighter
            side="player"
            name={player?.name || 'Operative'}
            currentHp={player?.currentHp}
            maxHp={userMaxHp}
            hpPercent={currentHpPercentage}
            stats={playerCombatStats}
            timer={playerTimer}
            effects={safeEffects}
            avatarConfig={avatarConfig}
            avatarSelection={avatarSelection}
          />
          <div className="combat-overlay__vs" aria-hidden="true">VS</div>
          <CombatFighter
            side="enemy"
            name={enemy?.name || 'Unknown Foe'}
            level={enemyLevel}
            currentHp={enemy?.currentHp}
            maxHp={enemyHealthMax}
            hpPercent={enemyHpPercent}
            stats={enemyCombatStats.filter(stat => !stat.isZero)}
            timer={enemyTimer}
            effects={safeEffects}
            enemy={enemy}
          />
        </div>

        {player?.currentAction === 'FLEEING' && (
          <div className="combat-overlay__flee">
            <div
              className="combat-overlay__flee-status"
              aria-atomic="true"
              aria-live="polite"
              role="status"
            >
              {fleeProgress?.status || 'Finding an opening…'}
            </div>
            {fleeProgress?.timer && (
              <LiveCountdownBar
                label={`Flee attempt ${fleeProgress.attempt || 1}`}
                timer={fleeProgress.timer}
                variant="flee"
              />
            )}
          </div>
        )}

        <div className="combat-overlay__log" aria-label="Combat log">
          <div className="combat-overlay__log-header">
            <span>Latest exchange</span>
            {earlierLog.length > 0 && (
              <details className="combat-overlay__history">
                <summary>History ({earlierLog.length})</summary>
                <ul>
                  {earlierLog.map((entry, idx) => (
                    <li key={`combat-history-${idx}`}>{entry}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
          <ul className="combat-overlay__recent-log">
            {latestLog.length > 0 ? latestLog.map((entry, idx) => (
              <li key={`combat-log-${idx}`}>{entry}</li>
            )) : (
              <li className="combat-overlay__log-empty">Weapons are being reconsidered.</li>
            )}
          </ul>
        </div>

        <div className="combat-overlay__actions">
          <button type="button" onClick={() => onSetDesiredAction(fleeAction)}>
            {fleeLabel}
          </button>
        </div>
      </section>
    </div>
  );
}

const CombatOverlay = React.memo(CombatOverlayComponent);

export default CombatOverlay;
